import { useState } from "react";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { useArtists, usePageMeta } from "@/hooks";
import { ArtistCard } from "@/components/artists";
import { ServicesAccordion } from "@/components/ServicesAccordion";
import { ImageGallery } from "@/components/ImageGallery";
import { ReviewsCarousel } from "@/components/ReviewsCarousel";
import { HomeHero } from "@/components/home/HomeHero";
import { Loader2 } from "lucide-react";

const ARTIST_CARD_VARIANTS = {
  hidden: { opacity: 0, y: 28, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as const } },
};

export function HomePage() {
  usePageMeta({
    title: "StudioComm — Custom Art Commissions, Every Line Drawn by Hand",
    description:
      "Custom character art, reference sheets, emotes and VTuber assets — every line drawn by hand by real artists, with no AI at any stage. Commissions since 2020.",
    canonicalPath: "/",
  });
  const { data: artists, isLoading, error } = useArtists();
  const [selectedSpecialization, setSelectedSpecialization] = useState<string | null>(null);

  // Filter artists by tag selection only (closed artists already filtered by API).
  // Compare case-insensitively because stored tags are mixed-case but the
  // ServicesAccordion emits lowercase normalized values (fix for H-FILT-1).
  const filteredArtists = artists?.filter((artist) => {
    if (!selectedSpecialization) return true;
    const needle = selectedSpecialization.trim().toLowerCase();
    return artist.tags?.some((t) => t.trim().toLowerCase() === needle);
  });

  const handleSpecializationClick = (tag: string) => {
    setSelectedSpecialization(selectedSpecialization === tag ? null : tag);
  };

  return (
    // reducedMotion="user": framer drops transform animations for visitors
    // who ask their OS for less motion; opacity fades still play.
    <MotionConfig reducedMotion="user">
    <div>
      <HomeHero />

      {/* Services Accordion */}
      <ServicesAccordion
        onTagClick={handleSpecializationClick}
        selectedTag={selectedSpecialization}
      />

      {/* Artists Section */}
      <section id="artists" className="py-16 bg-[var(--color-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-[var(--font-display)] text-3xl text-[var(--color-text)] text-center mb-4">
            Our <em className="text-[var(--color-primary)]">Artists</em>
          </h2>
          <p className="text-center text-[var(--color-text-muted)] mb-8 max-w-2xl mx-auto">
            Discover talented artists ready to bring your vision to life
          </p>

          {/* Active Filter Indicator */}
          {selectedSpecialization && (
            <div className="flex justify-center mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--color-bg-card)] border border-[var(--color-primary)] rounded-full text-sm">
                Filtering: <strong>{selectedSpecialization}</strong>
                <button
                  onClick={() => setSelectedSpecialization(null)}
                  aria-label="Clear filter"
                  className="pressable focus-ring rounded-full ml-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  ✕
                </button>
              </span>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
              <span className="ml-3 text-[var(--color-text-muted)]">Loading artists...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div data-prerender="skip" className="text-center py-20">
              <p className="text-red-500 mb-4">Failed to load artists</p>
              <button
                onClick={() => window.location.reload()}
                className="pressable focus-ring px-4 py-2 rounded-lg bg-[var(--color-primary)] text-[var(--color-bg)] hover:bg-[var(--color-primary-light)]"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Artist Grid */}
          {filteredArtists && filteredArtists.length > 0 && (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
              variants={{ show: { transition: { staggerChildren: 0.06 } } }}
            >
              <AnimatePresence mode="popLayout">
                {filteredArtists.map((artist) => (
                  <motion.div
                    key={artist.id}
                    layout
                    variants={ARTIST_CARD_VARIANTS}
                    exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.2 } }}
                    transition={{ layout: { type: "spring", stiffness: 260, damping: 32 } }}
                  >
                    <ArtistCard artist={artist} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Empty State */}
          {filteredArtists && filteredArtists.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[var(--color-text-muted)]">
                {selectedSpecialization
                  ? `No artists found for "${selectedSpecialization}". Try a different filter.`
                  : "No artists available at the moment. Check back soon!"
                }
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Portfolio Gallery */}
      <ImageGallery
        selectedTag={selectedSpecialization}
        onClearParentTag={() => setSelectedSpecialization(null)}
      />

      {/* Client Reviews */}
      <ReviewsCarousel />
    </div>
    </MotionConfig>
  );
}
